import { type ReactNode, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import { useCancelTurn, useGenerate, usePatchModel } from "../api/models";
import type { ModelDetail } from "../api/types";
import { creditsWord, rowMessageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Field, ImageLightbox, Input, Tabs } from "../ui";
import { type Vec3, sizeCm } from "../viewer/math";
import { WAIT_TITLE, isResize } from "./HistoryPanel";
import { PROMPT_MAX, SIZE_ERROR, metresOf, shortfall } from "./NewDesignPage";

export const SCULPTED_NOTE =
  "Sculpted designs cannot be changed by instruction, tuned with sliders or given other surfaces. To change one, edit the description and generate again.";
export const TEXTURE_ALT = "The object's texture";
export const TEXTURE_NOTE =
  "If you see painted-in shadows or highlights, try again: they will look wrong under the ad's lighting.";
export const GENERATING_NOTE =
  "This can take a few minutes. You can leave this page.";
export const STOP_FREE = "Stopping now costs nothing.";
export const REVIEW_QUESTION =
  "Does the picture look evenly lit, with no shadows painted in?";
export const APPLY_NOTE =
  "Apply rebuilds the object at the new size in under a minute, free.";
/** The longest side of a sculpted object whose size nobody stated, in cm. */
export const DEFAULT_LONGEST_CM = "30";
export const NAME_MAX = 80;
/** The panel's **Try again**, which the review's **Try again** focuses. */
export const TRY_AGAIN_ID = "sculpted-try-again";

const LINE = "m-0 text-sm text-slate-700";
const DISABLED = "disabled:cursor-not-allowed disabled:opacity-50";

/** The description of the newest generated step: what **Try again** asks for
 *  once more. A change of size and a restore are not generations. */
export function generatedDescription(detail: ModelDetail): string {
  const step = detail.revisions.find(
    (row) => row.source === "mesh" && !isResize(row),
  );
  return step?.instruction ?? "";
}

/** The object's colour picture, flat: the browser's copy of the one artwork
 *  a sculpted step has. Null while nothing is built. */
export function textureOf(detail: ModelDetail): string | null {
  const skins = detail.current?.asset.defaults.skins ?? {};
  for (const skin of Object.values(skins)) {
    const url = skin.files["web_base_color.jpg"];
    if (url) return url;
  }
  return null;
}

/** The picture of the step that is shown. */
export function currentPicture(detail: ModelDetail): string | null {
  const n = detail.current?.n;
  const step = detail.revisions.find((row) => row.n === n);
  return step?.view_url ?? detail.current?.asset.files["thumb.jpg"] ?? null;
}

/** Puts the keyboard on the panel's **Try again**, once the window that asked
 *  has closed (a closing window gives focus back to its opener first). */
export function focusTryAgain(): void {
  window.setTimeout(() => document.getElementById(TRY_AGAIN_ID)?.focus(), 0);
}

/** What the review shows before a sculpted design is added
 *  ([sculpted §5.4]): the texture flat beside the picture of the current
 *  step, and the question. Generated textures can carry painted-in light,
 *  which no later step can remove. Stacked under 640 px. */
export function ReviewPictures({
  detail,
}: {
  detail: ModelDetail;
}): JSX.Element {
  const texture = textureOf(detail);
  const picture = currentPicture(detail);
  // Stacked on a phone, and small enough there that the question and the
  // name stay on the screen with them.
  const frame =
    "mx-auto block aspect-square w-full max-w-[200px] rounded-lg bg-slate-100 object-contain sm:max-w-none";
  return (
    <div className="flex flex-col gap-3" data-testid="sculpted-review">
      <div className="grid gap-3 sm:grid-cols-2">
        {texture ? (
          <img src={texture} alt={TEXTURE_ALT} className={frame} />
        ) : null}
        {picture ? (
          <img src={picture} alt="The object as it is now" className={frame} />
        ) : null}
      </div>
      <p className="m-0 text-sm font-semibold text-slate-800">
        {REVIEW_QUESTION}
      </p>
    </div>
  );
}

/** **Texture**: the colour picture flat, full size on a click. */
function Texture({ url }: { url: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <section className="flex flex-col gap-3" data-testid="texture-panel">
      <button
        type="button"
        className="block w-full cursor-zoom-in rounded-lg p-0"
        onClick={() => setOpen(true)}
      >
        <img
          src={url}
          alt={TEXTURE_ALT}
          className="block aspect-square w-full rounded-lg bg-slate-100 object-contain"
        />
      </button>
      <p className={LINE}>{TEXTURE_NOTE}</p>
      {open ? (
        <ImageLightbox
          src={url}
          alt={TEXTURE_ALT}
          label={TEXTURE_ALT}
          fit="contain"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

/** The stored size as the field shows it: centimetres, or empty. */
function cmText(metres: number | null): string {
  return metres === null ? "" : String(Math.round(metres * 1e4) / 100);
}

/** **Details**: the name, the longest side with **Apply**, and how the
 *  object came out ([sculpted §5.3]). A new size rebuilds the object, free,
 *  as a "Resized" step; it asks for no generation. */
function Details({ detail }: { detail: ModelDetail }): JSX.Element {
  const rename = usePatchModel(detail.id);
  const resize = usePatchModel(detail.id);
  const [name, setName] = useState(detail.name);
  useEffect(() => setName(detail.name), [detail.name]);
  const stored = cmText(detail.real_size_m);
  const [size, setSize] = useState(stored);
  useEffect(() => setSize(stored), [stored]);
  const [wrong, setWrong] = useState(false);

  const busy = detail.state !== "idle";
  const report = detail.current?.report;
  const sizeM = report?.size_m;
  const triangles = report?.triangles;
  const source = report?.source_triangles;

  const saveName = () => {
    const typed = name.trim();
    if (!typed || typed === detail.name) {
      setName(detail.name);
      return;
    }
    rename.mutate({ name: typed }, { onError: () => setName(detail.name) });
  };
  const apply = () => {
    const metres = metresOf(size);
    if (Number.isNaN(metres)) {
      setWrong(true);
      return;
    }
    setWrong(false);
    if (busy || metres === detail.real_size_m) return;
    resize.mutate({ real_size_m: metres }, { onError: () => setSize(stored) });
  };

  return (
    <section className="flex flex-col gap-3" data-testid="details-panel">
      <Field label="Name">
        <Input
          value={name}
          maxLength={NAME_MAX}
          disabled={rename.isPending}
          onChange={(event) => setName(event.target.value)}
          onBlur={saveName}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      </Field>
      {rename.isError ? (
        <ErrorLine error={rename.error} context="design" />
      ) : null}
      <form
        className="flex flex-col gap-2"
        // The page says what is wrong with a size, in its own words.
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <div className="flex items-end gap-2">
          <Field
            label="Longest side (cm)"
            error={wrong ? SIZE_ERROR : undefined}
            className="min-w-0 flex-1"
          >
            <Input
              type="number"
              inputMode="decimal"
              min={0.5}
              max={5000}
              step="any"
              placeholder={DEFAULT_LONGEST_CM}
              value={size}
              invalid={wrong}
              onChange={(event) => setSize(event.target.value)}
            />
          </Field>
          <Button
            type="submit"
            className={`min-h-[44px] shrink-0 md:min-h-0 ${DISABLED}`}
            disabled={busy || resize.isPending}
            title={busy ? WAIT_TITLE : undefined}
            aria-busy={resize.isPending ? true : undefined}
          >
            Apply
          </Button>
        </div>
        <p className="m-0 text-xs text-slate-600">{APPLY_NOTE}</p>
      </form>
      {resize.isError ? (
        <ErrorLine error={resize.error} context="design" />
      ) : null}
      {Array.isArray(sizeM) ? (
        <p className={LINE}>Size: {sizeCm(sizeM as Vec3)}</p>
      ) : null}
      {typeof triangles === "number" ? (
        <p className={LINE}>
          Detail: {triangles.toLocaleString("en-US")} triangles
          {report?.decimated === true && typeof source === "number"
            ? `, simplified from ${source.toLocaleString("en-US")}`
            : ""}
        </p>
      ) : null}
    </section>
  );
}

/** What stands where a built design has its instruction box
 *  ([sculpted §5.2]): the description, **Try again** and **Generate from this
 *  description**, each labelled with its price, and **Stop** while a step
 *  runs. A stopped generation costs nothing. */
function Generate(props: {
  detail: ModelDetail;
  available: number | null;
}): JSX.Element {
  const { detail, available } = props;
  const { id, latest, current, mesh_fee: fee } = detail;
  const generate = useGenerate(id);
  const cancel = useCancelTurn(id);

  const description = generatedDescription(detail);
  const [text, setText] = useState(description);
  // A new generation's description is the box's, until the user edits it.
  useEffect(() => setText(description), [description]);

  const busy = detail.state !== "idle";
  const running =
    latest !== null &&
    (latest.status === "queued" || latest.status === "running");
  const short =
    available !== null && available < fee ? shortfall(available, fee) : null;
  const typed = text.trim();
  const same = typed === description.trim();
  const blocked = busy || short !== null || generate.isPending;
  const title = busy ? WAIT_TITLE : undefined;
  const price = creditsWord(fee);

  // With nothing built yet the page says what became of the step where the
  // 3D view will be.
  const ended =
    latest !== null &&
    current !== null &&
    latest.source === "mesh" &&
    (latest.status === "failed" || latest.status === "refused");
  // A stop that arrives after the end is not an error: the design is asked
  // for again and shows how the step ended.
  const stopError =
    cancel.error instanceof ApiError && cancel.error.code === "invalid_state"
      ? null
      : cancel.error;
  const fieldId = `description-${id}`;

  return (
    <section className="flex flex-col gap-2" data-testid="sculpted-box">
      {ended ? (
        <div role="alert" className="flex flex-col gap-1" data-testid="outcome">
          <p className={LINE}>{rowMessageFor(latest.error_code)}</p>
          {latest.status === "refused" && latest.refusal_reason ? (
            <p className={LINE}>{latest.refusal_reason}</p>
          ) : null}
        </div>
      ) : null}
      <label htmlFor={fieldId} className="tb-field-label">
        Description
      </label>
      <textarea
        id={fieldId}
        className="tb-input min-h-[84px] min-w-0 resize-y"
        rows={3}
        value={text}
        maxLength={PROMPT_MAX}
        onChange={(event) => setText(event.target.value)}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 max-md:[&_.tb-btn]:min-h-[44px]">
        <Button
          id={TRY_AGAIN_ID}
          type="button"
          variant="primary"
          className={DISABLED}
          disabled={blocked || !same || description === ""}
          title={title}
          aria-busy={generate.isPending && same ? true : undefined}
          onClick={() => generate.mutate(undefined)}
        >
          Try again · {price}
        </Button>
        <Button
          type="button"
          className={DISABLED}
          disabled={blocked || same || typed === ""}
          title={title}
          aria-busy={generate.isPending && !same ? true : undefined}
          onClick={() => generate.mutate(typed)}
        >
          Generate from this description · {price}
        </Button>
      </div>
      {running && latest ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button
            type="button"
            className={`min-h-[44px] md:min-h-0 ${DISABLED}`}
            disabled={cancel.isPending}
            aria-busy={cancel.isPending ? true : undefined}
            onClick={() => cancel.mutate(latest.n)}
          >
            Stop
          </Button>
          <span className="text-sm text-slate-600" data-testid="stop-cost">
            {STOP_FREE}
          </span>
        </div>
      ) : null}
      {short ? <ErrorLine error={short} context="generate" /> : null}
      {generate.isError ? (
        <ErrorLine error={generate.error} context="generate" />
      ) : null}
      {stopError ? <ErrorLine error={stopError} context="design" /> : null}
      <p className="m-0 text-sm text-slate-600" data-testid="sculpted-note">
        {SCULPTED_NOTE}
      </p>
    </section>
  );
}

/** The right column and the bottom block of a sculpted design
 *  ([sculpted §5.2], [ux §8.18]). Offered: the texture, the name and the
 *  size, **Try again**, a new description, **Stop**. Not offered, and the
 *  page says so: instructions, sliders and other surfaces. The two parts are
 *  cells of the design page's grid. */
export function SculptedPanel(props: {
  detail: ModelDetail;
  /** The organisation's available credits; null when not known. */
  available: number | null;
  /** The history, which is a tab on a phone. */
  history?: ReactNode;
}): JSX.Element {
  const { detail, history } = props;
  const texture = textureOf(detail);
  const tabs = [
    ...(history ? [{ id: "history", label: "History" }] : []),
    // With nothing built there is no texture to look at.
    ...(texture ? [{ id: "texture", label: "Texture" }] : []),
    { id: "details", label: "Details" },
  ];
  const [tab, setTab] = useState<string | null>(null);
  const active = tabs.some((t) => t.id === tab) ? tab! : tabs[0].id;

  return (
    <>
      <div
        className="min-w-0 md:col-start-3 md:row-span-2 md:row-start-1"
        data-testid="design-tabs"
      >
        <Tabs tabs={tabs} active={active} onChange={setTab}>
          {active === "history" ? history : null}
          {active === "texture" && texture ? <Texture url={texture} /> : null}
          {active === "details" ? <Details detail={detail} /> : null}
        </Tabs>
      </div>
      <div
        className="min-w-0 md:col-start-2 md:row-start-2"
        data-testid="design-box"
      >
        <Generate detail={detail} available={props.available} />
      </div>
    </>
  );
}
